import { FunctionComponent, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";

import { ContentStatus } from "../schema/knowledgeDb";

interface EditableStringListTableProps {
	title: string;
	items: string[];
	onSave?: (items: string[]) => void;
	onDelete?: () => void;
	contentStatus?: ContentStatus;
	onApprove?: () => void;
	onUnapprove?: () => void;
}

interface ListForm {
	rows: { value: string }[];
}

export const EditableStringListTable: FunctionComponent<EditableStringListTableProps> = ({
	title,
	items,
	onSave,
	onDelete,
	contentStatus,
	onApprove,
	onUnapprove,
}) => {
	const [isEditing, setIsEditing] = useState(false);

	const toRows = (arr: string[]) => arr.map((value) => ({ value }));

	const { control, register, handleSubmit, reset } = useForm<ListForm>({
		defaultValues: { rows: toRows(items) },
	});

	const { fields, append, remove } = useFieldArray({ control, name: "rows" });

	const handleEdit = () => {
		reset({ rows: toRows(items) });
		setIsEditing(true);
	};

	const handleCancel = () => {
		setIsEditing(false);
		reset({ rows: toRows(items) });
	};

	const onSubmit = (data: ListForm) => {
		onSave?.(data.rows.map((r) => r.value));
		setIsEditing(false);
		if (contentStatus && contentStatus !== ContentStatus.HUMAN_APPROVED) {
			onApprove?.();
		}
	};

	return (
		<div className="rounded-xl border border-gray-300 bg-white mb-4">
			<div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
				<div className="flex items-center gap-2">
					<h3 className="text-sm font-semibold text-gray-900">{title}</h3>
					{contentStatus === ContentStatus.AI_DRAFTED && (
						<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-brand-white border border-blue-action/40 text-blue-action">
							✨ AI
						</span>
					)}
					{contentStatus === ContentStatus.DRAFT && (
						<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-status-warning-light border border-status-warning/40 text-status-warning">
							✎ Draft
						</span>
					)}
					{contentStatus === ContentStatus.HUMAN_APPROVED && (
						<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-status-success-light border border-status-success/40 text-status-success">
							✓ Approved
						</span>
					)}
				</div>
				{!isEditing && (
					<div className="flex items-center gap-2">
						{onApprove &&
							(contentStatus === ContentStatus.AI_DRAFTED ||
								contentStatus === ContentStatus.DRAFT) && (
								<button
									className="text-xs font-medium cursor-pointer text-status-warning hover:text-status-warning/80 px-2 py-1 rounded transition-colors"
									type="button"
									onClick={onApprove}
								>
									✓ Approve
								</button>
							)}
						{onUnapprove && contentStatus === ContentStatus.HUMAN_APPROVED && (
							<button
								className="text-xs font-medium cursor-pointer text-status-warning hover:text-status-warning/80 px-2 py-1 rounded transition-colors"
								type="button"
								onClick={onUnapprove}
							>
								✗ Unapprove
							</button>
						)}
						{onSave && (
							<button
								className="text-xs font-medium cursor-pointer text-text-meta hover:text-blue-action px-2 py-1 rounded transition-colors"
								type="button"
								onClick={handleEdit}
							>
								Edit
							</button>
						)}
						{onDelete && (
							<button
								className="text-xs font-medium cursor-pointer text-red-500 hover:text-red-500/80 px-2 py-1 rounded transition-colors"
								type="button"
								onClick={onDelete}
							>
								Delete
							</button>
						)}
					</div>
				)}
			</div>

			<div className="px-4 py-3">
				{isEditing ? (
					<form onSubmit={handleSubmit(onSubmit)}>
						<div className="space-y-2">
							{fields.map((field, index) => (
								<div key={field.id} className="flex items-center gap-2">
									<input
										className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
										{...register(`rows.${index}.value`)}
									/>
									<button
										className="text-red-500 hover:text-red-500/80 text-xs cursor-pointer shrink-0"
										type="button"
										onClick={() => remove(index)}
									>
										✕
									</button>
								</div>
							))}
						</div>
						<div className="flex justify-between mt-3">
							<button
								className="text-xs font-medium cursor-pointer text-brand-light hover:text-blue-action transition-colors"
								type="button"
								onClick={() => append({ value: "" })}
							>
								+ Add row
							</button>
							<div className="flex gap-2">
								<button
									className="px-3 py-1.5 text-xs font-medium cursor-pointer text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
									type="button"
									onClick={handleCancel}
								>
									Cancel
								</button>
								<button
									className="px-3 py-1.5 text-xs font-medium cursor-pointer text-white bg-brand-light rounded-lg hover:bg-blue-action transition-colors"
									type="submit"
								>
									Save
								</button>
							</div>
						</div>
					</form>
				) : items.length === 0 ? (
					<span className="text-gray-400 italic">No items added yet.</span>
				) : (
					<ul className="list-disc list-inside space-y-1">
						{items.map((item, idx) => (
							<li key={idx} className="text-sm text-gray-700">
								{item}
							</li>
						))}
					</ul>
				)}
			</div>
		</div>
	);
};
